import { Lock, ArrowUpRight, Phone, Mail, MapPin, ShieldCheck } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { scrollToId } from "@/components/Nav";
import { SEMPLIFICAPP_URL, PRIVACY_URL, CONTACTS, AIUTI_PDF_URL, RNA_URL } from "@/data/content";

export default function Footer() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const goSection = (href) => {
    if (pathname !== "/") {
      sessionStorage.setItem("scrollTo", href);
      navigate("/");
    } else {
      scrollToId(href);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-ink text-white border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
        <div>
          <img src="/assets/logo-nuovo.png" alt="Semplificaffitto.it" className="h-8 w-auto brightness-0 invert mb-5" />
          <p className="text-sm text-white/55 leading-relaxed max-w-xs">
            Servizio di gestione affitti di Edilstudio S.r.l., società immobiliare attiva a Scandicci
            dal 2012. Ci prendiamo cura del proprietario e dell'inquilino, a Firenze e provincia.
          </p>
          <p className="text-xs text-white/35 mt-4 leading-relaxed">
            Edilstudio S.r.l. · operativi dal 2012 · Fabio Pesce & Giulia Resuttana
          </p>
        </div>

        <div>
          <p className="eyebrow text-azure mb-5">Navigazione</p>
          <ul className="space-y-3 text-sm text-white/60">
            {[
              ["Vantaggi", "#vantaggi"],
              ["Come funziona", "#come-funziona"],
              ["Costi", "#costi"],
              ["Blog", "#blog"],
              ["Chi siamo", "#chi-siamo"],
              ["Contatti", "#contatti"],
            ].map(([label, href]) => (
              <li key={href}>
                <a
                  href={href}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToId(href);
                  }}
                  className="hover:text-white transition-colors duration-300"
                  data-testid={`footer-link-${label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="eyebrow text-azure mb-5">Contatti</p>
          <ul className="space-y-4 text-sm text-white/60">
            <li>
              <a href={CONTACTS.telHref} data-testid="footer-phone-link" className="flex items-center gap-3 hover:text-white transition-colors duration-300">
                <Phone size={15} className="text-azure shrink-0" />
                {CONTACTS.tel}
              </a>
            </li>
            <li>
              <a href={`mailto:${CONTACTS.email}`} data-testid="footer-email-link" className="flex items-center gap-3 hover:text-white transition-colors duration-300">
                <Mail size={15} className="text-azure shrink-0" />
                {CONTACTS.email}
              </a>
            </li>
            <li>
              <a href={CONTACTS.mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-address-link" className="flex items-start gap-3 hover:text-white transition-colors duration-300">
                <MapPin size={15} className="text-azure shrink-0 mt-0.5" />
                {CONTACTS.address}
              </a>
            </li>
            <li className="text-xs text-white/40">P.IVA {CONTACTS.piva}</li>
          </ul>
        </div>

        <div>
          <p className="eyebrow text-azure mb-5">Area riservata</p>
          <p className="text-sm text-white/55 leading-relaxed mb-5">
            Sei già nostro cliente? Monitora la tua locazione dal portale o dalla app.
          </p>
          <a
            href={SEMPLIFICAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-reserved-area-btn"
            className="inline-flex items-center gap-2 bg-azure hover:bg-brandblue text-white text-sm font-semibold rounded-full px-6 py-3 transition-colors duration-300"
          >
            <Lock size={14} />
            Semplificapp.it
            <ArrowUpRight size={15} />
          </a>
          <div className="mt-5">
            <Link
              to="/admin"
              data-testid="footer-admin-link"
              className="inline-flex items-center gap-1.5 text-xs text-white/40 hover:text-white transition-colors duration-300"
            >
              <ShieldCheck size={13} />
              Accesso Admin
            </Link>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10" data-testid="trasparenza-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8 flex flex-col md:flex-row items-start md:items-center gap-6">
          <div className="flex-1">
            <p className="eyebrow text-azure mb-3">Trasparenza — Aiuti di Stato</p>
            <p className="text-xs text-white/45 leading-relaxed max-w-2xl">
              La società ha ricevuto nel corso degli esercizi 2019 e 2020 aiuti di Stato di cui
              all'art. 52, Legge n. 234/2012, soggetti all'obbligo di pubblicazione nel{" "}
              <a href={RNA_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-rna-link" className="text-white/70 underline underline-offset-2 hover:text-white">
                Registro Nazionale degli Aiuti di Stato
              </a>
              , sezione trasparenza.
            </p>
          </div>
          <a href={AIUTI_PDF_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-por-banner" className="shrink-0">
            <img src="/assets/por-creo.png" alt="POR CreO FESR Toscana 2014-2020 — Bando 2019" className="h-14 w-auto rounded-md bg-white p-1" />
          </a>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/40">
          <p>© {new Date().getFullYear()} Semplificaffitto.it — P.IVA {CONTACTS.piva} — Scandicci (FI). Tutti i diritti riservati.</p>
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="hover:text-white/70 transition-colors" data-testid="footer-privacy-link">
            Cookie e Privacy
          </a>
        </div>
      </div>
    </footer>
  );
}
